/**
 * SlideHubLanding
 *
 * Default home view for Slide Hub. Customer wanted a calm starting screen
 * after we initially shipped the busier "resume + slide feed" variant —
 * cut down to greeting + three action cards + the user's recent
 * presentations + recently uploaded decks. (#5)
 *
 * Routing intent for the three actions:
 *   - "Ny presentation" — clear builder, open "My presentations" so the
 *     user picks a starting point or names a new one.
 *   - "Sök slides" / "Bläddra bibliotek" — open the All Slides view.
 *     Both land on the same destination on purpose; the labels signal
 *     intent ("I want to search" vs "I want to browse") and the
 *     library handles both interactions.
 */

import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { SlideSenseDeck } from "../types"

import { FilePlus, Layers, Library, Search } from "@/components/icons"

import { SlideThumbnailImage } from "./SlideThumbnailImage"
import { useCollections } from "../hooks/use-collections"
import { useDeckCovers } from "../hooks/use-deck-covers"
import { useDecks } from "../hooks/use-decks"
import { useSlideSenseNav } from "../hooks/use-slide-sense-nav"
import { formatSlideSenseRelativeDate } from "../utils/date-format"

interface SlideHubLandingProps {
  /** Caller wires this so clicking a deck opens the library focused there. */
  onOpenDeck?: (deckId: string, folderId?: string) => void
  /** Loads a saved collection into the builder + opens the library. */
  onOpenCollection?: (collectionId: string) => void
  /** Clear builder (with confirm) and open My presentations. */
  onStartNewPresentation?: () => void
}

const MAX_DECKS = 4
const MAX_COLLECTIONS = 4

// Only decks that finished processing have rendered slides (and thus a cover
// thumbnail). Showing pending/processing/failed decks here would render a run
// of placeholder tiles that makes the section look broken.
const READY_DECK_STATUSES = new Set<SlideSenseDeck["processing_status"]>([
  "completed",
  "completed_with_errors",
])

export function SlideHubLanding({
  onOpenDeck,
  onOpenCollection,
  onStartNewPresentation,
}: SlideHubLandingProps) {
  const { t } = useTranslation()
  const selectAllSlides = useSlideSenseNav((s) => s.selectAllSlides)
  const selectMyPresentations = useSlideSenseNav((s) => s.selectMyPresentations)

  const { data: decks } = useDecks({})
  const { data: collections } = useCollections()

  const recentDecks = useMemo(() => {
    if (!decks) return []
    return [...decks]
      .filter((d) => READY_DECK_STATUSES.has(d.processing_status))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      .slice(0, MAX_DECKS)
  }, [decks])

  // Resolve covers only for the decks actually rendered, not the whole tenant.
  const recentDeckIds = useMemo(() => recentDecks.map((d) => d.id), [recentDecks])
  const { data: deckCovers } = useDeckCovers(recentDeckIds)

  const sortedCollections = useMemo(() => {
    if (!collections) return []
    return [...collections].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
  }, [collections])
  const recentCollections = sortedCollections.slice(0, MAX_COLLECTIONS)

  return (
    <div className="bg-background h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10 sm:py-14">
        <header className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("slideSense.landing.greeting")}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl text-sm sm:text-base">
            {t("slideSense.landing.tagline")}
          </p>
        </header>

        {/* Three primary action cards. Visual weight goes to the first
            (orange icon background) since it is the only one that mutates
            state — the other two are pure navigation. */}
        <section className="mb-14 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ActionCard
            description={t("slideSense.landing.actionNewDescription")}
            icon={<FilePlus className="h-5 w-5" />}
            title={t("slideSense.landing.actionNew")}
            tone="primary"
            onClick={() => onStartNewPresentation?.()}
          />
          <ActionCard
            description={t("slideSense.landing.actionSearchDescription")}
            icon={<Search className="h-5 w-5" />}
            title={t("slideSense.landing.actionSearch")}
            onClick={selectAllSlides}
          />
          <ActionCard
            description={t("slideSense.landing.actionBrowseDescription")}
            icon={<Library className="h-5 w-5" />}
            title={t("slideSense.landing.actionBrowse")}
            onClick={selectAllSlides}
          />
        </section>

        {/* My presentations first — the customer wanted the user's own
            work above the company-wide deck list since it is what most
            people open the home screen for. */}
        <section className="mb-10">
          <SectionHeader
            actionLabel={recentCollections.length > 0 ? t("slideSense.landing.seeAll") : undefined}
            title={t("slideSense.landing.myRecent")}
            onAction={selectMyPresentations}
          />
          {recentCollections.length === 0 ? (
            <EmptyHint message={t("slideSense.landing.myRecentEmpty")} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {recentCollections.map((c) => (
                <CollectionRowCard
                  key={c.id}
                  coverSlideId={c.slide_ids[0]}
                  name={c.name}
                  slideCount={c.slide_count}
                  updatedAt={c.updated_at}
                  onClick={() => onOpenCollection?.(c.id)}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionHeader
            actionLabel={recentDecks.length > 0 ? t("slideSense.landing.seeAll") : undefined}
            title={t("slideSense.landing.recentDecks")}
            onAction={selectAllSlides}
          />
          {recentDecks.length === 0 ? (
            <EmptyHint message={t("slideSense.landing.recentDecksEmpty")} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {recentDecks.map((deck) => (
                <DeckRowCard
                  key={deck.id}
                  coverSlideId={deckCovers?.[deck.id]}
                  filename={deck.original_filename}
                  slideCount={deck.slide_count}
                  uploadedAt={deck.created_at}
                  onClick={() => onOpenDeck?.(deck.id, deck.folder_id)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

interface ActionCardProps {
  title: string
  description: string
  icon: React.ReactNode
  tone?: "default" | "primary"
  onClick: () => void
}

function ActionCard({ title, description, icon, tone = "default", onClick }: ActionCardProps) {
  const isPrimary = tone === "primary"
  return (
    <button
      className="card-interactive group bg-card hover:border-primary/40 focus-visible:ring-ring flex flex-col items-start gap-2 rounded-lg border p-5 text-left shadow-sm focus-visible:ring-2 focus-visible:outline-none"
      type="button"
      onClick={onClick}
    >
      <span
        className={
          isPrimary
            ? "border-foreground bg-primary text-primary-foreground inline-flex h-9 w-9 items-center justify-center rounded-md border"
            : "border-foreground bg-accent text-accent-foreground inline-flex h-9 w-9 items-center justify-center rounded-md border"
        }
      >
        {icon}
      </span>
      <span className="text-base font-semibold">{title}</span>
      <span className="text-muted-foreground text-sm">{description}</span>
    </button>
  )
}

function SectionHeader({
  title,
  actionLabel,
  onAction,
}: {
  title: string
  /** When omitted, the section renders just its title — no overflow link. */
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div className="mb-3 flex items-end justify-between">
      <h2 className="text-lg font-semibold">{title}</h2>
      {actionLabel && onAction && (
        <button
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring text-xs font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
          type="button"
          onClick={onAction}
        >
          {actionLabel} →
        </button>
      )}
    </div>
  )
}

function EmptyHint({ message }: { message: string }) {
  return (
    <div className="border-border bg-muted/30 text-muted-foreground rounded-lg border border-dashed px-4 py-8 text-center text-sm">
      {message}
    </div>
  )
}

function DeckRowCard({
  filename,
  slideCount,
  uploadedAt,
  coverSlideId,
  onClick,
}: {
  filename: string
  slideCount: number
  uploadedAt: string
  /** First slide of the deck — resolves to the cover thumbnail. */
  coverSlideId?: string
  onClick: () => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <button
      className="card-interactive group bg-card hover:border-primary/40 focus-visible:ring-ring flex flex-col gap-2 overflow-hidden rounded-lg border text-left shadow-sm focus-visible:ring-2 focus-visible:outline-none"
      type="button"
      onClick={onClick}
    >
      <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
        <SlideThumbnailImage
          alt={filename}
          fallbackIcon={<Layers className="text-muted-foreground/40 h-8 w-8" />}
          slideId={coverSlideId}
        />
      </div>
      <div className="space-y-1 px-3 pb-3">
        <div className="truncate text-sm font-semibold" title={filename}>
          {filename}
        </div>
        <div className="text-muted-foreground text-xs">
          {t("slideSense.slideCount", { count: slideCount })}
          {" · "}
          {formatSlideSenseRelativeDate(uploadedAt, i18n.language)}
        </div>
      </div>
    </button>
  )
}

function CollectionRowCard({
  name,
  slideCount,
  updatedAt,
  coverSlideId,
  onClick,
}: {
  name: string
  slideCount: number
  updatedAt: string
  /** First slide of the saved presentation — resolves to the cover thumbnail. */
  coverSlideId?: string
  onClick: () => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <button
      className="card-interactive group bg-card hover:border-primary/40 focus-visible:ring-ring flex flex-col gap-2 overflow-hidden rounded-lg border text-left shadow-sm focus-visible:ring-2 focus-visible:outline-none"
      type="button"
      onClick={onClick}
    >
      <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
        <SlideThumbnailImage
          alt={name}
          fallbackIcon={<FilePlus className="text-muted-foreground/40 h-8 w-8" />}
          slideId={coverSlideId}
        />
      </div>
      <div className="space-y-1 px-3 pb-3">
        <div className="truncate text-sm font-semibold" title={name}>
          {name}
        </div>
        <div className="text-muted-foreground text-xs">
          {t("slideSense.slideCount", { count: slideCount })}
          {" · "}
          {formatSlideSenseRelativeDate(updatedAt, i18n.language)}
        </div>
      </div>
    </button>
  )
}
